import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { 
  Activity, 
  RefreshCw, 
  TrendingUp, 
  AlertTriangle, 
  CheckCircle, 
  Loader2,
  Eye,
  FileText,
  Shield,
  Sparkles
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface AgentEvent {
  id: string;
  event_type: string;
  job_type: string | null;
  title: string;
  description: string | null;
  money_impact: number | null;
  severity: string;
  status: string;
  metadata: any;
  created_at: string;
}

const eventTypeConfig: Record<string, { icon: typeof Activity; color: string; bgColor: string }> = {
  monitor: { icon: Eye, color: 'text-primary', bgColor: 'bg-primary/10' },
  reconcile: { icon: RefreshCw, color: 'text-success', bgColor: 'bg-success/10' },
  forecast: { icon: TrendingUp, color: 'text-accent', bgColor: 'bg-accent/10' },
  insight: { icon: FileText, color: 'text-warning', bgColor: 'bg-warning/10' },
  alert: { icon: AlertTriangle, color: 'text-destructive', bgColor: 'bg-destructive/10' },
};

const formatRelativeTime = (dateStr: string) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

interface AgentTimelineProps {
  limit?: number;
  showHeader?: boolean;
  className?: string;
}

export function AgentTimeline({ limit = 10, showHeader = true, className }: AgentTimelineProps) {
  const { user } = useAuth();
  const [events, setEvents] = useState<AgentEvent[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchEvents = async () => {
    if (!user) return;
    setLoading(true);
    
    const { data, error } = await supabase
      .from('agent_events')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (!error && data) {
      setEvents(data as AgentEvent[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvents();

    const channel = supabase
      .channel('agent-events')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'agent_events' },
        (payload) => {
          const newEvent = payload.new as Record<string, unknown>;
          if (newEvent && newEvent.user_id === user?.id) {
            setEvents((prev) => [newEvent as unknown as AgentEvent, ...prev].slice(0, limit));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, limit]);

  if (loading) {
    return (
      <Card className={cn("rounded-3xl", className)}>
        {showHeader && (
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Agent Activity
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (events.length === 0) {
    return (
      <Card className={cn("rounded-3xl", className)}>
        {showHeader && (
          <CardHeader className="pb-4">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Agent Activity
            </CardTitle>
          </CardHeader>
        )}
        <CardContent className="text-center py-12">
          <div className="w-16 h-16 rounded-3xl bg-surface mx-auto mb-4 flex items-center justify-center">
            <Shield className="h-8 w-8 text-muted-foreground/50" />
          </div>
          <h4 className="font-medium text-foreground mb-2">No Activity Yet</h4>
          <p className="text-sm text-muted-foreground max-w-[200px] mx-auto">
            Run an agent scan to see Sentinel's activity here
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("rounded-3xl", className)}>
      {showHeader && (
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Agent Activity
            </CardTitle>
            <Button variant="ghost" size="sm" onClick={fetchEvents} className="h-8 w-8 p-0 rounded-xl">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
      )}
      <CardContent className="p-0">
        <ScrollArea className="h-[360px]">
          <div className="p-4 pt-2 space-y-2">
            <AnimatePresence mode="popLayout">
              {events.map((event, idx) => {
                const config = eventTypeConfig[event.event_type] || eventTypeConfig.monitor;
                const Icon = config.icon;
                
                return (
                  <motion.div
                    key={event.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: idx * 0.03 }}
                    className={cn(
                      'flex items-start gap-3 p-4 rounded-2xl transition-colors hover:bg-surface',
                      idx === 0 && 'bg-surface/50'
                    )}
                  >
                    <div className={cn('p-2.5 rounded-2xl shrink-0', config.bgColor)}>
                      <Icon className={cn('h-4 w-4', config.color)} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className="text-sm font-medium text-foreground line-clamp-1">{event.title}</p>
                        <span className="text-xs text-muted-foreground shrink-0">
                          {formatRelativeTime(event.created_at)}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                          {event.description}
                        </p>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {event.money_impact !== null && event.money_impact !== 0 && (
                          <Badge variant="secondary" className="text-xs font-mono rounded-xl">
                            {event.money_impact > 0 ? '+' : ''}{formatCurrency(event.money_impact)}
                          </Badge>
                        )}
                        {event.status === 'running' && (
                          <Badge variant="outline" className="text-xs rounded-xl">
                            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                            Running
                          </Badge>
                        )}
                        {event.status === 'completed' && event.severity === 'success' && (
                          <CheckCircle className="h-3.5 w-3.5 text-success" />
                        )}
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
